import { useNuxtApp } from '#app'
import { ref, computed } from 'vue'
import { useRuntimeConfig } from '#app'
import type { Component } from 'vue'

interface ComponentInfo {
  name: string
  path: string
  relativePath?: string // For dev
  friendlyName?: string
  autoImportName?: string // For production
}

// Cache for component metadata (not imported, just info)
let componentRegistry: ComponentInfo[] | null = null

// Cache for actually loaded components
const loadedComponents = new Map<string, any>()

export const useBuilderComponents = (friendlyNames?: string[]) => {
  const config = useRuntimeConfig()
  const { prefix } = config.public.builderIO.components as { prefix: string; dir: string; enabled: boolean }

  const components = ref<any[]>([])
  const isLoading = ref(false)
  let loadPromise: Promise<void> | null = null

  const loadComponents = async () => {
    if (isLoading.value) return loadPromise
    isLoading.value = true

    loadPromise = (async () => {
      try {
        // Handle production differently - use pre-generated loader
        if (!import.meta.env.DEV) {
          try {
            // @ts-ignore
            const { loadBuilderComponents } = await import('#build/builder/component-loader.mjs')
            const loadedComponentsList = loadBuilderComponents(friendlyNames)
            
            if (!loadedComponentsList || loadedComponentsList.length === 0) {
              console.warn('No components loaded from component loader')
              components.value = []
              return
            }
            
            // Add to cache
            for (const component of loadedComponentsList) {
              const cacheKey = component.friendlyName || component.name
              loadedComponents.set(cacheKey, component)
            }
            
            components.value = loadedComponentsList
            //console.log(`Loaded ${loadedComponentsList.length} components in production`)
            return
          } catch (error) {
            console.error('Failed to load component loader:', error)
            components.value = []
            return
          }
        }

        // Development mode - use dynamic discovery
        // Get component registry from API if we don't have it
        if (!componentRegistry) {
          try {
            // @ts-ignore - $fetch is available at runtime
            componentRegistry = await globalThis.$fetch('/api/builder-components')
            //console.log('Discovered components:', componentRegistry?.length || 0)
          } catch (error) {
            console.error('Failed to fetch component registry:', error)
            componentRegistry = []
          }
        }

        if (!componentRegistry || componentRegistry.length === 0) {
          console.warn('No components found in registry')
          components.value = []
          return
        }

        // Determine which components to load
        let componentsToLoad: ComponentInfo[] = []
        
        if (friendlyNames && friendlyNames.length > 0 && componentRegistry) {
          // Try to match requested names with component friendly names
          componentsToLoad = friendlyNames.map(requestedName => {
            // Find all components that match the requested name
            const candidates = componentRegistry!.filter(c => {
              const friendlyNameMatch = c.friendlyName === requestedName ||
                c.friendlyName?.toLowerCase() === requestedName.toLowerCase()
              return friendlyNameMatch
            })
            
            if (candidates.length === 0) {
              // Try partial match if no exact match
              const partialMatch = componentRegistry!.find(c => 
                c.friendlyName?.toLowerCase().includes(requestedName.toLowerCase()) ||
                requestedName.toLowerCase().includes(c.friendlyName?.toLowerCase() || '')
              )
              if (partialMatch) {
                return partialMatch
              }
              console.warn(`Component "${requestedName}" not found in registry`)
              return null
            } else if (candidates.length === 1) {
              // Only one match, use it
              return candidates[0]
            } else {
              // Multiple matches, try to pick the best one
              // Prefer components from certain directories based on common patterns
              const preferredDirs = ['healthyCoffee', 'healthyCoffee1', 'healtyCoffee22Builder']
              
              for (const dir of preferredDirs) {
                const match = candidates.find(c => c.relativePath && c.relativePath.includes(dir))
                if (match) {
                  console.log(`Multiple components named "${requestedName}" found, using ${match.relativePath}`)
                  return match
                }
              }
              
              // If no preferred match, just use the first one
              console.log(`Multiple components named "${requestedName}" found, using ${candidates[0].relativePath || candidates[0].path}`)
              return candidates[0]
            }
          }).filter(Boolean) as ComponentInfo[]
        } else {
          // Load all components
          componentsToLoad = componentRegistry || []
        }

        //console.log(`Loading ${componentsToLoad.length} components:`, componentsToLoad.map(c => c.friendlyName))

        // Load only the requested components
        const loadedComponentsList = []
        
        for (const componentInfo of componentsToLoad) {
          const cacheKey = componentInfo.friendlyName || componentInfo.name
          
          // Check if already loaded
          if (loadedComponents.has(cacheKey)) {
            loadedComponentsList.push(loadedComponents.get(cacheKey))
            continue
          }
          
          try {
            if (import.meta.env.DEV) {
              // In development, use dynamic imports with raw paths
              const componentPath = componentInfo.path
              const nuxtUrl = `/_nuxt${componentPath}?builder=true`
              const componentUrl = `/_nuxt${componentPath}`
              
              // First, import the actual Vue component
              const componentModule = await import(/* @vite-ignore */ componentUrl)
              const vueComponent = componentModule.default
              
              // Then, import the builder metadata
              const builderDataModule = await import(/* @vite-ignore */ nuxtUrl)
              
              if (builderDataModule.default && !builderDataModule.default.ignored) {
                // Create the proper component structure with the actual Vue component
                const component = {
                  friendlyName: builderDataModule.default.friendlyName,
                  name: builderDataModule.default.friendlyName, // Use friendlyName as name
                  component: vueComponent, // Use the actual Vue component, not async
                  inputs: builderDataModule.default.inputs || [],
                  canHaveChildren: builderDataModule.default.canHaveChildren !== false,
                  // Include any other properties from builder metadata if they exist
                  ...(builderDataModule.default.description && { description: builderDataModule.default.description }),
                  ...(builderDataModule.default.schema && { schema: builderDataModule.default.schema })
                }
                
                // Cache it
                loadedComponents.set(cacheKey, component)
                loadedComponentsList.push(component)
              }
            }
          } catch (error) {
            console.warn(`Failed to load component ${componentInfo.name}:`, error)
          }
        }

        components.value = loadedComponentsList
      } catch (error) {
        console.error('Failed to load builder components:', error)
      } finally {
        isLoading.value = false
      }
    })()

    return loadPromise
  }

  // Start loading immediately
  const initialLoadPromise = loadComponents()

  // Return both the computed ref and a way to await loading
  return {
    components: computed(() => components.value),
    ready: initialLoadPromise
  }
}
